import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, PlayCircle, Lock, Clock, BookOpen } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function VideoPlayer({ video }) {
  return (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-black" data-testid="learning-player">
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
        <iframe
          title={video.title}
          src={video.embed_url}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-white">{video.title}</h3>
        {video.description && <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{video.description}</p>}
      </div>
    </div>
  );
}

export default function Learning() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    api.get("/learning")
      .then((r) => {
        const cs = r.data.courses || [];
        setCourses(cs);
        const firstWithVideo = cs.find((c) => c.videos.length > 0) || cs[0] || null;
        setActiveCourse(firstWithVideo || null);
        setActiveVideo(firstWithVideo?.videos?.[0] || null);
      })
      .catch((e) => {
        if (e.response?.status === 403) setLocked(true);
        else formatError(e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-zinc-500" data-testid="learning-loading">Caricamento…</div>;
  }

  if (locked) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="learning-locked">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/10 flex items-center justify-center mb-5">
          <Lock className="text-amber-400" size={28} />
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-3">Learning — riservato ai VIP</h1>
        <p className="text-zinc-400 mb-8">
          I percorsi di formazione video sul trading sono disponibili solo per gli abbonati Premium.
        </p>
        <Link to="/pricing" data-testid="learning-upgrade-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors">
          Passa a Premium
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="learning-page">
      <div className="mb-8">
        <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 mb-4">
          <GraduationCap size={12} /> LEARNING
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Formazione sul Trading</h1>
        <p className="text-zinc-400 mt-2">Percorsi video esclusivi per gli abbonati Raeinexchange.</p>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-20 text-zinc-500" data-testid="learning-empty">
          <BookOpen size={40} className="mx-auto mb-4 opacity-50" />
          I contenuti di formazione saranno disponibili a breve.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {activeVideo ? (
              <VideoPlayer video={activeVideo} />
            ) : (
              <div className="rounded-lg border border-dashed border-white/10 p-12 text-center text-zinc-500" data-testid="learning-no-video">
                Nessun video in questo corso.
              </div>
            )}
          </div>

          <div className="space-y-6">
            {courses.map((c) => (
              <div key={c.id} className="bg-[#141414] border border-white/10 rounded-md overflow-hidden" data-testid={`learning-course-${c.id}`}>
                <div className="px-4 py-3 border-b border-white/5">
                  <h2 className="text-base font-semibold text-white">{c.title}</h2>
                  {c.description && <p className="text-xs text-zinc-500 mt-0.5">{c.description}</p>}
                </div>
                <div className="divide-y divide-white/5">
                  {c.videos.length === 0 && (
                    <div className="px-4 py-3 text-xs text-zinc-600">Nessun video</div>
                  )}
                  {c.videos.map((v, i) => {
                    const isActive = activeVideo?.id === v.id;
                    return (
                      <button
                        key={v.id}
                        data-testid={`learning-video-item-${v.id}`}
                        onClick={() => { setActiveCourse(c); setActiveVideo(v); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${isActive ? "bg-emerald-500/10" : "hover:bg-white/5"}`}
                      >
                        <PlayCircle size={18} className={`mt-0.5 shrink-0 ${isActive ? "text-emerald-400" : "text-zinc-500"}`} />
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm truncate ${isActive ? "text-emerald-300" : "text-zinc-200"}`}>
                            {i + 1}. {v.title}
                          </div>
                          {v.duration && (
                            <div className="flex items-center gap-1 text-[11px] text-zinc-500 mt-0.5">
                              <Clock size={11} /> {v.duration}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
